import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  changeOwnPassword,
  fetchUserById,
  updateUserById,
  updateUserActivitiesApi,
  createUser,
} from "../services/usersService";
import { updateStoredAuthUserFromUser } from "../utils/authUser";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const USERS_QK = ["users"];

const resolveErrorMessage = (error) => {
  if (!error) return "Profil non modifié";
  if (typeof error?.data === "string" && error.data.trim()) return error.data;
  if (error?.data && typeof error.data === "object") {
    if (typeof error.data.detail === "string" && error.data.detail.trim()) {
      return error.data.detail;
    }
    const [firstKey] = Object.keys(error.data);
    const firstValue = error.data[firstKey];
    if (Array.isArray(firstValue) && firstValue[0]) return String(firstValue[0]);
    if (typeof firstValue === "string" && firstValue.trim()) return firstValue;
  }
  return error?.message || "Profil non modifié";
};

export const useUser = (id, options = {}) =>
  useQuery({
    queryKey: [...USERS_QK, id],
    queryFn: () => fetchUserById(id),
    enabled: !!id,
    ...options,
  });

export const useUpdateUser = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => updateUserById(id, payload),
    onSuccess: (data, variables) => {
      if (variables?.id) {
        qc.invalidateQueries({ queryKey: [...USERS_QK, variables.id] });
      }
      qc.invalidateQueries({ queryKey: USERS_QK });
      if (data) {
        updateStoredAuthUserFromUser(data);
      } else if (variables?.payload) {
        updateStoredAuthUserFromUser({
          id: variables.id,
          ...variables.payload,
        });
      }
      toast.success("Profil modifié", { autoClose: 3000 });
    },
    onError: (error) =>
      toast.error(resolveErrorMessage(error), {
        autoClose: 3000,
      }),
  });
};

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_QK });
      toast.success("Utilisateur bien ajouté", { autoClose: 3000 });
    },
    onError: () =>
      toast.error(
        "Utilisateur non ajouté, Vérifier votre connexion et réessayer",
        { autoClose: 3000 },
      ),
  });
};

/**
 * Change the signed-in user's own password.
 *
 * Refresh tokens are revoked server-side, so other sessions stop working —
 * the current one keeps its access token until it expires naturally.
 */
export const useChangeOwnPassword = () =>
  useMutation({
    mutationFn: changeOwnPassword,
    onSuccess: () =>
      toast.success("Mot de passe modifié avec succès", { autoClose: 3000 }),
    onError: (err) =>
      toast.error(
        resolveApiErrorMessage(err, "Modification du mot de passe impossible"),
        { autoClose: 5000 }
      ),
  });

export const useUpdateUserActivities = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activities }) => updateUserActivitiesApi(id, activities),
    onSuccess: (_data, variables) => {
      if (variables?.id) qc.invalidateQueries({ queryKey: [...USERS_QK, variables.id] });
      qc.invalidateQueries({ queryKey: USERS_QK });
      toast.success("Activités enregistrées", { autoClose: 3000 });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err, "Activités non enregistrées"), { autoClose: 5000 });
    },
  });
};
